import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { t } from "../i18n/index.ts";
import type { Option } from "../lib/data.ts";
import { ignore } from "../lib/ignore.ts";
import { useId } from "../lib/uid.ts";
import { CheckIcon } from "./icons.tsx";
import { placePopover } from "./popover.ts";
import { CONTROL } from "./styles.ts";

const DEBOUNCE_MS = 150;

interface Props<O extends Option> {
  label: string;
  /** Search; gets aborted when the query changes. */
  search: (q: string, signal: AbortSignal) => Promise<O[]>;
  onSelect: (o: O) => void;
  placeholder?: string;
  /**
   * Its options always there, under it in what holds it (a panel of their own: no popup on a popup), the first ones
   * before anything's typed, in a box of a fixed height that scrolls; focused as it appears.
   */
  inline?: boolean;
  /** Focused as it appears (asked for: a search opened by its icon). */
  autoFocus?: boolean;
  /** Inline: the option picked (checked in the list, which stays as it is). */
  picked?: string | undefined;
}

/** Runs `search` (debounced, aborting the previous one) while open. */
function useOptions<O extends Option>(q: string, open: boolean, search: Props<O>["search"]): O[] | null {
  const [opts, setOpts] = useState<O[] | null>(null);
  const fn = useRef(search);
  fn.current = search;
  useEffect(() => {
    if (!open) {
      return;
    }
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      fn.current(q, ctl.signal).then(setOpts, ignore); // aborted or failed: keep the previous options
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [q, open]);
  return opts;
}

/**
 * The options, in the top layer (over a dialog, never cut off by what scrolls around the input): under the input, or
 * above it when there's more room there, as wide as it, scrolling inside past the room there is.
 */
function Listbox<O extends Option>(p: {
  id: string;
  label: string;
  opts: O[] | null;
  active: number;
  anchor: HTMLElement | null;
  onPick: (o: O, i: number) => void;
  onPoint: (i: number) => void;
  inline: boolean;
  picked: string | undefined;
}) {
  const box = useRef<HTMLDivElement>(null);
  // Placed each time it changes (its height follows what's found).
  useLayoutEffect(() => {
    const el = box.current;
    if (p.inline || el === null || p.anchor === null) {
      return;
    }
    if (!el.matches(":popover-open")) {
      el.showPopover();
    }
    el.style.setProperty("width", `${p.anchor.offsetWidth}px`);
    placePopover(el, p.anchor);
  });
  return (
    <div
      ref={box}
      id={p.id}
      role="listbox"
      aria-label={p.label}
      popover={p.inline ? undefined : "manual"}
      class={
        p.inline
          ? "h-64 overflow-auto overscroll-contain py-1 text-sm text-zinc-900"
          : "fixed m-0 max-h-64 max-w-[calc(100vw-1rem)] overflow-auto rounded-md border border-zinc-200 bg-white py-1 text-sm text-zinc-900 shadow-lg"
      }
    >
      {p.opts === null ? <p class="px-3 py-1.5 text-zinc-500">{t("common.loading")}</p> : null}
      {p.opts?.length === 0 ? <p class="px-3 py-1.5 text-zinc-500">{t("common.noMatches")}</p> : null}
      {p.opts?.map((o, i) => (
        <div
          key={o.value}
          id={`${p.id}-${i}`}
          role="option"
          tabIndex={-1}
          aria-selected={i === p.active}
          aria-checked={p.picked === undefined ? undefined : o.value === p.picked}
          // Named by its label; what's under it describes it.
          aria-label={o.label}
          aria-describedby={o.hint ? `${p.id}-${i}-hint` : undefined}
          // An icon beside the label and what's under it (the label's text a cell of its own, the hint the next).
          class={`grid cursor-pointer gap-x-2 px-3 py-1.5 [overflow-wrap:anywhere] aria-selected:bg-blue-50 aria-selected:text-blue-900 ${o.icon ? "grid-cols-[1rem_minmax(0,1fr)]" : "grid-cols-1"} ${p.picked === undefined ? "" : "pr-9 relative aria-checked:font-medium"}`}
          ref={(el) => {
            if (i === p.active) {
              el?.scrollIntoView?.({ block: "nearest" });
            }
          }}
          onMouseMove={() => {
            if (i !== p.active) {
              p.onPoint(i);
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault(); // keep focus in the input
            p.onPick(o, i);
          }}
        >
          {p.picked === o.value ? <CheckIcon size={16} class="absolute top-2 right-3 text-blue-700" /> : null}
          {o.icon ? <span class="row-span-2 mt-0.5 flex size-4 items-center justify-center">{o.icon}</span> : null}
          {o.label}
          {o.hint ? (
            <span id={`${p.id}-${i}-hint`} class="text-xs text-zinc-500">
              {o.hint}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Async autocomplete (ARIA 1.2 combobox with a listbox popup). Picking an option clears the input. */
export function Combobox<O extends Option>(p: Props<O>) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const shown = open || (p.inline ?? false);
  const opts = useOptions(q, shown, p.search);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only as it first appears.
  useEffect(() => {
    if (p.inline || p.autoFocus) {
      input.current?.focus();
    }
  }, []);
  const pick = (o: O | undefined) => {
    if (o) {
      p.onSelect(o);
      // Showing what's picked, the list stays as it is (the pick checked in it); else the input's cleared.
      if (p.picked === undefined) {
        setQ("");
        setOpen(false);
      }
    }
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const n = opts?.length ?? 0;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (n === 0 ? 0 : (a + (e.key === "ArrowDown" ? 1 : n - 1)) % n));
    } else if (e.key === "Enter" && shown) {
      e.preventDefault();
      pick(opts?.[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };
  return (
    <div class={p.inline ? "flex flex-col gap-1" : "relative"}>
      <input
        ref={input}
        type="text"
        role="combobox"
        aria-label={p.label}
        aria-expanded={shown}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={shown && opts?.[active] ? `${id}-list-${active}` : undefined}
        placeholder={p.placeholder}
        class={`${CONTROL} h-9`}
        value={q}
        onInput={(e) => {
          setQ(e.currentTarget.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {shown ? (
        <Listbox
          id={`${id}-list`}
          label={p.label}
          opts={opts}
          active={active}
          anchor={input.current}
          onPick={(o, i) => {
            setActive(i);
            pick(o);
          }}
          onPoint={setActive}
          inline={p.inline ?? false}
          picked={p.picked}
        />
      ) : null}
    </div>
  );
}
